import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Button from "../../component/button/button"
import IconsColor from "../../util/iconsColor"
import utilStyles from "../../asset/styles/util/util.module.scss"
import styles from "../../asset/styles/quotation/newQuotation.module.scss"
import QuotationBasicDetails, { QuotationBasicSaveIntent } from "../../component/quotation/QuotationBasicDetails"
import JourneyDetails, { JourneyDetailsSaveIntent } from "../../component/quotation/JourneyDetails"
import PricingSummary, { PricingSummarySaveIntent } from "../../component/quotation/PricingSummary"
import { Grid } from "@mui/material"
import InputText from "../../component/inputText/inputText"
import { useQuotation } from "../../context/quotationContext"
import { useUserDetail } from "../../context/userDetailContext"
import { useSnackbar } from "../../context/snackbarContext"
import { agent } from "../../apiClient/api"
import { normalizeIsoDate } from "../../component/quotation/JourneyDetails"
import { MdModeEdit } from "react-icons/md";
import { toTitleCase } from "util/textcase"
import { buildQuotationPreviewPath } from "util/quotationPreviewPathUtil"
import CreateVersionReasonPopup from "component/quotation/createVersionReasonPopup"
import DuplicateQuotationSkippedHotelsPopup from "component/quotation/duplicateQuotationSkippedHotelsPopup"
import useCreateQuotationVersion from "component/quotation/useCreateQuotationVersion"
import {
  getQuotationStatusFlags,
  QUOTATION_STATUS,
  resolvePreservedSaveIntent,
} from "./newQuotationStatus"
import {QuotationStatusVisualVariant} from "../../component/quotation/quotationStatusVariant";

const steps = ["Basic Details", "Journey Details", "Pricing Summary"]

const NewQuotation: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeStep, setActiveStep] = useState(0)
  const {
    quotationId,
    setQuotationId,
    resetQuotationContext,
    createdBy,
    setCreatedBy,
    quotationName: contextQuotationName,
    setQuotationName,
    setJourneyDays,
    setAdditionalCosts,
    setPackageServiceCharge,
    setFlagWithTip,
    setQuotationBasicDetailsApiResponse,
    currentQuotationStatusObj,
    setCurrentQuotationStatusObj
  } = useQuotation()
  const { userDetail } = useUserDetail()
  const { showError } = useSnackbar()
  const pendingNameSaveRef = useRef(false)
  const [headerQuotationName, setHeaderQuotationName] = useState("")
  const [headerQuotationNameDraft, setHeaderQuotationNameDraft] = useState("")
  const [headerStaCode, setHeaderStaCode] = useState("")
  const [isEditingHeaderName, setIsEditingHeaderName] = useState(false)
  const [isQuotationReadyToSend, setIsQuotationReadyToSend] = useState(false)
  const [isQuotationSent, setIsQuotationSent] = useState(false)
  const [currentQuotationStatus, setCurrentQuotationStatus] = useState<number>(QUOTATION_STATUS.DRAFT)
  const [isQuotationStatusReady, setIsQuotationStatusReady] = useState(
    () => !searchParams.get("quotation_id"),
  )
  const [flagQuotationBasicDetailsApiRespReady, setFlagQuotationBasicDetailsApiRespReady] = useState(false);
  const [versionNumber, setVersionNumber] = useState<number>(1);

  const basicDetailsSaveHandlerRef = useRef<((intent?: QuotationBasicSaveIntent) => Promise<string | null>) | null>(null)
  const basicDetailsRefetchHandlerRef = useRef<(() => Promise<void>) | null>(null)
  const journeyDetailsSaveHandlerRef = useRef<((intent?: JourneyDetailsSaveIntent) => Promise<boolean>) | null>(null)
  const pricingSummarySaveHandlerRef = useRef<((intent?: PricingSummarySaveIntent) => Promise<boolean>) | null>(null)
  const isEditMode = quotationId.length > 0
  const isFirstStep = activeStep === 0
  const isLastStep = activeStep === steps.length - 1
  const urlQuotationId = searchParams.get("quotation_id") || ""
  const currentSaveIntent = resolvePreservedSaveIntent(currentQuotationStatus)

  // Hydrate context from URL on mount.
  // - Edit mode (`quotation_id` present): set context quotationId.
  // - Create mode (`quotation_id` absent): reset stale context from previous quotation.
  useEffect(() => {
    if (urlQuotationId) {
      if (urlQuotationId !== quotationId) setQuotationId(urlQuotationId)
      return
    }
    if (quotationId) resetQuotationContext()
  }, [quotationId, resetQuotationContext, setQuotationId, urlQuotationId])

  // Always hydrate edit mode from the URL id. The quotation context can already contain
  // the same id when returning from Preview, but local status flags still start fresh.
  useEffect(() => {
    let cancelled = false

    if (!urlQuotationId) {
      setCurrentQuotationStatus(QUOTATION_STATUS.DRAFT)
      setIsQuotationReadyToSend(false)
      setIsQuotationSent(false)
      setFlagQuotationBasicDetailsApiRespReady(false)
      setIsQuotationStatusReady(true)
      return
    }

    setIsQuotationStatusReady(false)
    setFlagQuotationBasicDetailsApiRespReady(false)

    const hydrateExistingQuotation = async (): Promise<void> => {
      try {
        const response = await agent.QuotationBasicDetailsAPI.get(urlQuotationId, true)
        if (cancelled) return
        if(response.status && response.data) {
          if(response.data.quotation) {
            setCurrentQuotationStatusObj({
              quotation_status_id: response.data.quotation.quotation_status_id,
              quotation_status_name: response.data.quotation.quotation_status_name,
            })
            const status_id = Number(response?.data?.quotation?.quotation_status_id || 0)
            const flags = getQuotationStatusFlags(status_id)
            setCurrentQuotationStatus(status_id)
            setIsQuotationReadyToSend(flags.isReadyToSend)
            setIsQuotationSent(flags.isSent)
            setQuotationBasicDetailsApiResponse(response)
            setFlagQuotationBasicDetailsApiRespReady(true)
            setIsQuotationStatusReady(true)
            setVersionNumber((response.data.quotation.version_no))

            // TEMP (client request): allow editing quotations in any status, including Confirmed/Lost.
            // Restore this redirect to lock edit mode for status 5/6 again.
            // if([5,6].includes(response.data.quotation.quotation_status_id)) {
            //   navigate(`/quotation/view/${response.data.quotation.quotation_id}`)
            // }
          }
        }
        if (!response?.status) {
          throw new Error("Quotation status is unavailable.")
        }
      } catch {
        if (cancelled) return
        setIsQuotationReadyToSend(false)
        setIsQuotationSent(false)
        setCurrentQuotationStatus(QUOTATION_STATUS.DRAFT)
        setIsQuotationStatusReady(false)
        showError("Failed to load quotation details")
      }
    }

    void hydrateExistingQuotation()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQuotationBasicDetailsApiResponse, showError, urlQuotationId])

  // Previous mirrors a step-click: save current step → fetch prev step data → navigate.
  // Navigation always happens, even if the save fails (handleStepClick handles that).
  const handlePreviousStep = () => {
    if (activeStep === 0) return
    void handleStepClick(activeStep - 1)
  }

  const updateQuotationId = (savedQuotationId: string) => {
    if (!savedQuotationId || savedQuotationId === quotationId) return
    setQuotationId(savedQuotationId)
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev)
      nextParams.set("quotation_id", savedQuotationId)
      return nextParams
    })
  }

  /** Chain GET #9 right after Step 1 PUT succeeds so Step 2 mounts with data ready. */
  const fetchJourneyDetailsIntoContext = async (id: string): Promise<void> => {
    if (!id) return
    try {
      const response = await agent.QuotationJourneyDetailsAPI.get(id)
      const data = response?.status ? response.data : undefined
      if (!data) return
      const responseDays = data.days || []
      if (responseDays.length > 0) {
        setJourneyDays(responseDays.map((d) => ({ ...d, date: normalizeIsoDate(d.date) })))
      } else {
        setJourneyDays([])
      }
      if (Array.isArray(data.additional_costs)) setAdditionalCosts(data.additional_costs)
      if (typeof data.package_service_charge === "number") setPackageServiceCharge(String(data.package_service_charge))
      if (typeof (data as any).flag_with_tip === "boolean") setFlagWithTip(Boolean((data as any).flag_with_tip))
    } catch {
      // Swallow — JourneyDetails mount also tries; keep silent here.
    }
  }

  const handleNextStep = async () => {
    if (!isQuotationStatusReady) return
    if (activeStep === 0) {
      const savedId = await basicDetailsSaveHandlerRef.current?.(currentSaveIntent)
      if (!savedId) return
      // Chain #9 immediately after #8 success — Step 2 mounts with data ready.
      await fetchJourneyDetailsIntoContext(savedId)
    }
    if (activeStep === 1) {
      const saved = await journeyDetailsSaveHandlerRef.current?.(currentSaveIntent)
      if (!saved) return
    }
    setActiveStep((prev) => Math.min(prev + 1, steps.length - 1))
  }

  // Save whichever step is currently active, preserving the quotation status.
  // Never throws — navigation proceeds regardless of the result (per requirement).
  const saveActiveStep = async (): Promise<boolean> => {
    if (!isQuotationStatusReady) return false
    try {
      if (activeStep === 0) {
        const response = await basicDetailsSaveHandlerRef.current?.(currentSaveIntent)
        if (response === null) throw new Error("Validation not matched.")
      } else if (activeStep === 1) {
        const saved = await journeyDetailsSaveHandlerRef.current?.(currentSaveIntent)
        if (!saved) return false
      } else if (activeStep === 2) {
        const saved = await pricingSummarySaveHandlerRef.current?.(currentSaveIntent)
        if (!saved) return false
      }
      return true;
    } catch {
      return false;
      // Swallow — handlers already surface their own errors; we still navigate.
    }
  }

  // Load fresh data for the step we're moving into.
  // - Step 0 stays mounted (display:none) → needs an explicit refetch.
  // - Step 1 is pre-loaded into context before mount (matches handleNextStep).
  // - Step 2 (PricingSummary) self-fetches on mount, so nothing extra is needed.
  const fetchStepData = async (targetStep: number): Promise<void> => {
    try {
      if (targetStep === 0) {
        await basicDetailsRefetchHandlerRef.current?.()
      } else if (targetStep === 1) {
        await fetchJourneyDetailsIntoContext(quotationId)
      }
    } catch {
      // Swallow — each step also self-hydrates on mount as a fallback.
    }
  }

  // Stepper click: save current step → fetch target step data → switch to it.
  const handleStepClick = async (targetStep: number) => {
    if (targetStep === activeStep) return
    const flagMoveForward = await saveActiveStep()
    if (flagMoveForward) {
      await fetchStepData(targetStep)
      setActiveStep(targetStep)
    }
  }

  const handleHeaderSaveDraft = async () => {
    if (!isQuotationStatusReady) return
    if (activeStep === 0) {
      await basicDetailsSaveHandlerRef.current?.("draft")
      return
    }
    if (activeStep === 1) {
      await journeyDetailsSaveHandlerRef.current?.("draft")
      return
    }
    await pricingSummarySaveHandlerRef.current?.("draft")
  }

  const handleHeaderSaveQuotation = async () => {
    let loweredQuotationStatus = currentQuotationStatusObj?.quotation_status_name?.toLowerCase() as QuotationStatusVisualVariant;
    if(loweredQuotationStatus === "draft") loweredQuotationStatus = "ready to send";
    if (!isQuotationStatusReady) return
    if (isQuotationReadyToSend || (currentQuotationStatusObj && (currentQuotationStatusObj.quotation_status_name === "Sent" || currentQuotationStatusObj.quotation_status_name === "Follow Up"))) {
      if (activeStep === 0) {
        await basicDetailsSaveHandlerRef.current?.(loweredQuotationStatus ?? "ready to send")
        return
      }
      if (activeStep === 1) {
        await journeyDetailsSaveHandlerRef.current?.(loweredQuotationStatus ?? "ready to send")
        return
      }
    }
    // if (activeStep !== 2) return
    // const isJourneyValidForFinalize =
    //   Array.isArray(journeyDays) &&
    //   journeyDays.length > 0 &&
    //   journeyDays.every((day) => {
    //     const trips = Array.isArray(day?.trips) ? day.trips : []
    //     const hasTour = trips.some((trip) => Boolean(trip?.day_trip_id) && String((trip as any)?._trip_kind || "") === "tour")
    //     const hasTransfer = trips.some((trip) => Boolean(trip?.day_trip_id) && String((trip as any)?._trip_kind || "") === "transfer")
    //     const hasTripOrTransfer = hasTour || hasTransfer || trips.some((trip) => Boolean(trip?.day_trip_id))

    //     // Hotels are optional: a quotation can be finalized without any hotel.
    //     // We still require a Tour/Transfer selection on every day.
    //     return hasTripOrTransfer
    //   })
    // if (!isJourneyValidForFinalize) {
    //   showError("For each day, please select at least one Tour/Transfer before saving quotation.")
    //   return
    // }
    const ok = await pricingSummarySaveHandlerRef.current?.(loweredQuotationStatus ?? "ready to send")
    if (ok) {
      navigate(buildQuotationPreviewPath(quotationId))
    }
  }

  // Persist current edits before cloning from this quotation.
  const saveCurrentStepBeforeNewVersion = async () => {
    const preserveIntent: QuotationBasicSaveIntent | JourneyDetailsSaveIntent | PricingSummarySaveIntent =
      currentSaveIntent
    if (activeStep === 0) await basicDetailsSaveHandlerRef.current?.(preserveIntent as QuotationBasicSaveIntent)
    if (activeStep === 1) await journeyDetailsSaveHandlerRef.current?.(preserveIntent as JourneyDetailsSaveIntent)
    if (activeStep === 2) await pricingSummarySaveHandlerRef.current?.(preserveIntent as PricingSummarySaveIntent)
  }

  // The new version opens on Basic Details; changing the URL id re-hydrates edit mode for it.
  const openNewVersionEditMode = (newQuotationId: string) => {
    setActiveStep(0)
    setQuotationId(newQuotationId)
    setSearchParams((prev) => {
      const nextParams = new URLSearchParams(prev)
      nextParams.set("quotation_id", newQuotationId)
      return nextParams
    })
  }

  const createVersion = useCreateQuotationVersion({
    quotationId,
    beforeCreate: saveCurrentStepBeforeNewVersion,
    onOpenNewVersion: openNewVersionEditMode,
  })

  const handleCreateNewVersion = () => {
    if (!isQuotationStatusReady || !quotationId || !isQuotationReadyToSend || !isQuotationSent) return
    void createVersion.openReasonPopup()
  }

  const openHeaderNameEditor = () => {
    setHeaderQuotationNameDraft(headerQuotationName)
    setIsEditingHeaderName(true)
  }

  const closeHeaderNameEditor = (commitChanges: boolean) => {
    if (commitChanges) {
      const next = headerQuotationNameDraft.trim()
      const changed = next !== headerQuotationName
      setHeaderQuotationName(next)
      // Sync to context so the next Step-1 save closure picks up the new name.
      setQuotationName(next)
      // Skip auto-save when there's no quotationId yet — that path should go through
      // the explicit Step-1 create flow rather than be triggered by a header rename.
      if (changed && quotationId) pendingNameSaveRef.current = true
    } else {
      setHeaderQuotationNameDraft(headerQuotationName)
    }
    setIsEditingHeaderName(false)
  }

  // Header rename → fires PUT #8 once context.quotationName has caught up to the new value.
  // Two-step (mark pending, then watch context) avoids saving with the stale name.
  useEffect(() => {
    if (!pendingNameSaveRef.current) return
    if (contextQuotationName !== headerQuotationName) return
    if (!isQuotationStatusReady) return
    pendingNameSaveRef.current = false
    basicDetailsSaveHandlerRef.current?.(currentSaveIntent).catch(() => undefined)
  }, [contextQuotationName, currentSaveIntent, headerQuotationName, isQuotationStatusReady])

  // For new quotation (no id), show logged-in user name as "Created by".
  // For existing quotation, QuotationBasicDetails hydrates DB value.
  useEffect(() => {
    if (quotationId) return
    const first = userDetail?.user_first_name?.trim() || ""
    const last = userDetail?.user_last_name?.trim() || ""
    const fullName = `${first} ${last}`.trim()
    setCreatedBy(fullName)
  }, [quotationId, userDetail, setCreatedBy])

  const handleHeaderMetaChange = useCallback(
    ({ quotationName, staCode }: { quotationName: string; staCode: string; }) => {
      if (!isEditingHeaderName) {
        setHeaderQuotationName((prev) => (prev === quotationName ? prev : quotationName))
      }
      setHeaderStaCode((prev) => (prev === staCode ? prev : staCode))
    },
    [isEditingHeaderName]
  )

  const handleBackToPreview = () => {
    if (quotationId) {
      navigate(buildQuotationPreviewPath(quotationId))
    } else {
      navigate("/quotation")
    }
  }

  const displayQuotationName = headerQuotationName.trim() || "Quotation Name"
  const displayStaCode = headerStaCode.trim() || (isEditMode ? "STA CODE" : "NEW")

  return (
    <div className={utilStyles.pageContainer}>
      <div className={styles.wrapper}>
        <div className={styles.topHeader}>
          <div className={styles.titleRow}>
            <button className={styles.backButton} type="button" onClick={handleBackToPreview} title="Back to quotation preview">
              <IconsColor iconName="chevronLeft" width="18" height="18" fillColor="none" strokeColor="#1E293B" />
            </button>
            <div className={styles.headerNameBlock}>
              {isEditingHeaderName ? (
                <InputText
                  label="Quotation Name"
                  hideLabel={true}
                  noMargin={true}
                  height="34px"
                  width="300px"
                  value={toTitleCase(headerQuotationNameDraft ?? "")}
                  onChange={(e) => setHeaderQuotationNameDraft(e.target.value)}
                  onBlur={() => closeHeaderNameEditor(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") closeHeaderNameEditor(true)
                    if (e.key === "Escape") closeHeaderNameEditor(false)
                  }}
                  placeholder="Quotation Name"
                />
              ) : (
                <>
                  <h1 className={styles.pageTitle}>{toTitleCase(displayQuotationName)}</h1>
                  <button
                    type="button"
                    className={styles.editNameButton}
                    onClick={openHeaderNameEditor}
                    title="Edit quotation name"
                    disabled={!isQuotationStatusReady}
                  >
                    <span className={styles.editNameIcon}><MdModeEdit /></span>
                  </button>
                </>
              )}
            </div>
            <span className={styles.quotationTag}>{displayStaCode}</span>
            <span className={styles.createdBy}>Created by: {toTitleCase(createdBy)}</span>
            <span className={styles.createdBy}>Version: {versionNumber}</span>
          </div>
          <div className={styles.headerActions}>
            {isQuotationStatusReady && !isQuotationReadyToSend && !isQuotationSent && (
              <Button type="outlined" label="Save as Draft" onClick={handleHeaderSaveDraft} />
            )}
            {isQuotationStatusReady && ((isQuotationReadyToSend || isQuotationSent) || activeStep === 2) && (
              <Button type="contained" label="Save Quotation" onClick={handleHeaderSaveQuotation} />
            )}
            {isQuotationStatusReady && isQuotationReadyToSend && isQuotationSent && (
              <Button
                type="outlined"
                label={createVersion.isSubmitting ? "Creating..." : "Create New Version"}
                onClick={handleCreateNewVersion}
                disabled={createVersion.isSubmitting}
              />
            )}
          </div>
        </div>

        <div className={styles.stepperBar}>
          {steps.map((step, index) => {
            const isActive = index === activeStep
            return (
              <React.Fragment key={step}>
                <button
                  type="button"
                  className={`${styles.stepItem} ${isActive ? styles.activeStep : ""}`}
                  onClick={() => handleStepClick(index)}
                  disabled={!isQuotationStatusReady}
                >
                  <span className={styles.stepIndex}>{index + 1}</span>
                  <span className={styles.stepLabel}>{toTitleCase(step)}</span>
                </button>
                {index < steps.length - 1 && <span className={styles.stepDivider}>{"›"}</span>}
              </React.Fragment>
            )
          })}
        </div>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }} style={{ display: activeStep === 0 ? "block" : "none" }} aria-hidden={activeStep !== 0}>
            <QuotationBasicDetails
              quotationId={quotationId}
              onSaved={updateQuotationId}
              quotationNameOverride={isEditingHeaderName ? headerQuotationNameDraft : headerQuotationName}
              onHeaderMetaChange={handleHeaderMetaChange}
              onBindSaveHandler={(handler) => {
                basicDetailsSaveHandlerRef.current = handler
              }}
              onBindRefetchHandler={(handler) => {
                basicDetailsRefetchHandlerRef.current = handler
              }}
              flagQuotationBasicDetailsApiRespReady={flagQuotationBasicDetailsApiRespReady}
            />
          </Grid>
          {activeStep === 1 && (
            <Grid size={{ xs: 12, md: 12 }}>
              <JourneyDetails
                onBindSaveHandler={(handler) => {
                  journeyDetailsSaveHandlerRef.current = handler
                }}
              />
            </Grid>
          )}
          {activeStep === 2 && (
            <Grid size={{ xs: 12, md: 12 }}>
              <PricingSummary
                onBindSaveHandler={(handler) => {
                  pricingSummarySaveHandlerRef.current = handler
                }}
              />
            </Grid>
          )}
        </Grid>
        <div className={styles.stepActions}>
          <Button type="outlined" label="Previous" onClick={handlePreviousStep} disabled={isFirstStep || !isQuotationStatusReady} />
          <Button type="contained" label="Next" onClick={handleNextStep} disabled={isLastStep || !isQuotationStatusReady} />
        </div>
      </div>
      <CreateVersionReasonPopup {...createVersion.reasonPopup} fromVersionNo={versionNumber} />
      <DuplicateQuotationSkippedHotelsPopup {...createVersion.skippedHotelsPopup} />
    </div>
  )
}

export default NewQuotation
